/**
 * Request Board → the key button next to Refresh: the signed-in user's auth
 * token in a modal, for pasting into a Claude Code session that runs the
 * /request-board-* commands. The same token card as Profile → Auth token
 * (show / hide, copy, expiry), framed as a dialog.
 *
 * Shown only to the board's developers (`canSeeRequestBoardToken`).
 */
import { useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";

import ProfileAuthTokenCard from "../ProfileScreen/ProfileAuthTokenCard";
import { A } from "../admin/shared/adminTokens";

export default function AuthTokenDialog({ open, onClose }) {
  // The card's frame: title, the Show / Copy buttons, then a close button.
  const Frame = useCallback(
    ({ title, subtitle, action, children }) => (
      <Box sx={{ p: "20px 22px 22px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 18, fontWeight: 600, color: A.ink }}>{title}</Box>
            {subtitle ? (
              <Box sx={{ fontSize: 12.5, color: A.muted, mt: "4px", lineHeight: 1.5 }}>{subtitle}</Box>
            ) : null}
          </Box>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            {action}
            <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: A.muted }}>
              <CloseRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
        {children}
      </Box>
    ),
    [onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      {/* Mounted only while open, so the token is read fresh each time. */}
      {open ? <ProfileAuthTokenCard Card={Frame} /> : null}
    </Dialog>
  );
}

AuthTokenDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
